import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { CONTACT } from "../../data/content";
import { Reveal } from "../../lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", message: "", preferred_contact: "Email" };

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      setForm(initial);
      toast.success("Thank you — your message is on its way to Ffyona.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or email me directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-sage/40 rounded-none px-0 py-3 text-ink placeholder:text-ink-muted/70 focus:outline-none focus:border-sage-dark transition-colors duration-300";

  return (
    <section id="contact" className="py-24 lg:py-36 bg-cream-100" aria-labelledby="contact-heading">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        {/* Left — invitation + details */}
        <div className="lg:col-span-5">
          <Reveal><p className="overline text-sage-dark mb-5">Get in touch</p></Reveal>
          <Reveal delay={0.05}>
            <h2 id="contact-heading" className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] tracking-tight">
              Let's have a gentle first chat.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg text-ink-soft leading-relaxed">
              Send a short message below and I'll personally respond within one working day to
              arrange a suitable time. There's no pressure and no obligation.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 space-y-5">
              <a href={CONTACT.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 group">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white border border-sage-light/50 text-sage-dark group-hover:bg-sage-dark group-hover:text-cream transition-colors duration-300">
                  <Phone size={19} strokeWidth={1.5} />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-widest text-ink-muted">Phone</span>
                  <span className="text-ink group-hover:text-sage-dark transition-colors">{CONTACT.phone}</span>
                </span>
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-center gap-4 group">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white border border-sage-light/50 text-sage-dark group-hover:bg-sage-dark group-hover:text-cream transition-colors duration-300">
                  <Mail size={19} strokeWidth={1.5} />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-widest text-ink-muted">Email</span>
                  <span className="text-ink group-hover:text-sage-dark transition-colors break-all">{CONTACT.email}</span>
                </span>
              </a>
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white border border-sage-light/50 text-sage-dark">
                  <MapPin size={19} strokeWidth={1.5} />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-widest text-ink-muted">Location</span>
                  <span className="text-ink">{CONTACT.location}</span>
                  <span className="block text-sm text-ink-muted">{CONTACT.serviceModes}</span>
                </span>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Right — form */}
        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <div className="rounded-[2rem] bg-white shadow-float border border-sage-light/40 p-8 lg:p-12">
              {done ? (
                <div data-testid="contact-success" className="py-16 text-center">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-sage-light">
                    <Send size={24} className="text-sage-dark" />
                  </div>
                  <h3 className="mt-6 font-serif text-3xl text-ink">Thank you for reaching out.</h3>
                  <p className="mt-3 text-ink-soft max-w-md mx-auto">
                    Your message has been sent. I'll personally respond within one working day to
                    arrange a time that suits you.
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    className="mt-8 rounded-full border border-sage-light px-6 py-3 text-sm font-medium text-sage-dark hover:bg-cream-100 transition-colors"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="enquiry-form" className="space-y-8">
                  <div className="grid sm:grid-cols-2 gap-8">
                    <div>
                      <label htmlFor="name" className="block text-xs uppercase tracking-widest text-ink-muted mb-1">Name</label>
                      <input id="name" data-testid="input-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" required />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs uppercase tracking-widest text-ink-muted mb-1">Email</label>
                      <input id="email" type="email" data-testid="input-email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" required />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-xs uppercase tracking-widest text-ink-muted mb-1">Phone <span className="normal-case tracking-normal text-ink-muted/70">(optional)</span></label>
                    <input id="phone" data-testid="input-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="Your phone number" />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs uppercase tracking-widest text-ink-muted mb-1">How can I help?</label>
                    <textarea id="message" data-testid="input-message" value={form.message} onChange={set("message")} rows={4} className={`${inputCls} resize-none`} placeholder="Share as much or as little as you'd like…" required />
                  </div>

                  <div>
                    <span className="block text-xs uppercase tracking-widest text-ink-muted mb-3">Preferred contact method</span>
                    <div className="flex gap-3">
                      {["Email", "Phone"].map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          data-testid={`pref-${opt.toLowerCase()}`}
                          onClick={() => setForm((f) => ({ ...f, preferred_contact: opt }))}
                          className={`rounded-full px-6 py-2.5 text-sm font-medium border transition-all duration-300 ${
                            form.preferred_contact === opt
                              ? "bg-sage-dark text-cream border-sage-dark"
                              : "bg-transparent text-ink-soft border-sage-light hover:border-sage"
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="submit-enquiry"
                    className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-sage-dark hover:bg-sage-deep text-cream px-8 py-4 text-sm font-medium tracking-wide shadow-soft hover:shadow-float transition-all duration-300 active:scale-[0.98] disabled:opacity-60"
                  >
                    {loading ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />}
                    {loading ? "Sending…" : "Send your enquiry"}
                  </button>

                  <p className="text-sm text-ink-muted pt-2">
                    I'll personally respond within one working day to arrange a suitable time.
                  </p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
